import { Box, Grid, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useReducer } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CSV } from "../assets/icons/csv.svg";
import { ReactComponent as LeftArrow } from "../assets/icons/left-arrow.svg";
import VmVButton from "../components/VmVButtons";
import VmVDataArray from "../components/VmVDataArray";
import VmVLabel from "../components/VmVLabel";
import VmVPie from "../components/VmVPie";
import VmVSelect from "../components/VmVSelect";
import VmVTextInput from "../components/VmVTextInput";
import { colors, typography } from "../constants/theme";
import RegionDropDowData from "../dropdownsData/regionEhpad.json";
import { fetchData } from "../helpers/fetch";
import formatDropDownData from "../helpers/formatDropDown";

// Utilisons des identifiants de traduction au lieu de textes en dur
const arrayKeys = [
  "table.headers.regionName",
  "table.headers.establishmentName",
  "table.headers.catering",
  "table.headers.maintenance",
  "table.headers.animation",
  "table.headers.receptionAndAdministration",
  "table.headers.hotel",
];

interface IReducerState {
  text: string;
  region: string;
}

interface IReducerAction {
  type: string;
  payload: string;
}

const initialStat: IReducerState = {
  text: "",
  region: "",
};

const reducer = (
  state: IReducerState,
  action: IReducerAction,
): IReducerState => {
  switch (action.type) {
    case "SET_TEXT":
      return {
        ...state,
        text: action.payload,
      };
    case "SET_REGION":
      return {
        ...state,
        region: action.payload,
      };
    case "RESET": {
      return {
        text: "",
        region: "",
      };
    }

    default:
      return state;
  }
};

const ParticipationsAndRegionsDetails = (): JSX.Element => {
  const navigate = useNavigate();
  const [filters, dispatch] = useReducer(reducer, initialStat);
  const { t } = useTranslation();

  const { data } = useQuery({
    queryKey: ["regions/ehpad"],
    queryFn: async () =>
      await fetchData("regions/EHPAD?page=1&pageLimit=10000"),
  });
  // verifier le data du useQuery
  // useEffect(() => {
  //   console.log("data Regions", data);
  // }, [data]);

  // const { data: regionData } = useQuery({
  //   queryKey: ["drop_down/regions"],
  //   queryFn: async () => await fetchDropDownData("drop-downs/regions"),
  // });

  const formatData = (dataToFormat: any): any[] => {
    if (dataToFormat === undefined) return [];

    let formatedData: any[] = [];

    dataToFormat?.regionsStatsList.forEach((element: any) => {
      formatedData = [
        ...formatedData,
        {
          [t("table.headers.regionName")]: element?.name,
          [t("table.headers.establishmentName")]: element?.etablissement?.name,
          [t("table.headers.catering")]:
            element?.etablissement?.nombre_disponibilites_restauration,
          [t("table.headers.maintenance")]:
            element?.etablissement?.nombre_disponibilites_maintenance,
          [t("table.headers.animation")]:
            element?.etablissement?.nombre_disponibilites_animation,
          [t("table.headers.receptionAndAdministration")]:
            element?.etablissement
              ?.nombre_disponibilites_accueil_et_administration,
          [t("table.headers.hotel")]:
            parseInt(
              element?.etablissement
                ?.nombre_disponibilites_hotellerie_serveur ?? 0,
            ) +
            parseInt(
              element?.etablissement?.nombre_disponibilites_hotellerie_ASH ?? 0,
            ),
        },
      ];
    });

    return formatedData;
  };

  const filterData = (dataToFilter: any[], filters: IReducerState): any[] => {
    if (dataToFilter === undefined || dataToFilter.length === 0) return [];
    return dataToFilter
      .filter((value) => {
        return (value[t("table.headers.regionName")] as string)
          .toLowerCase()
          .includes(filters.region.toLowerCase());
      })
      .filter((value) => {
        return value[t("table.headers.establishmentName")]
          ?.toLowerCase()
          ?.includes(filters.text.toLowerCase());
      });
  };

  // Transformer l'array des titres pour utiliser les traductions
  const translatedCells = arrayKeys.map((key) => t(key));

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <VmVButton
        onClick={() => {
          navigate("/");
        }}
        type="back"
        LeftIcon={<LeftArrow stroke={colors.darkGrey[200]} />}
      >
        {t("common.back")}
      </VmVButton>
      <Box
        alignSelf="center"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Typography
          color={colors.darkBlue}
          sx={{ ...typography.title1 }}
          mb={8}
        >
          {t("pages.participationsAndRegions.title")}
        </Typography>
        <ParticipationsAndRegionsDetailsChart />
        <Box display="flex" flexDirection="column" gap={8}>
          <Box display="flex" justifyContent="center">
            <VmVTextInput
              value={filters.text}
              onChange={(event) => {
                dispatch({ type: "SET_TEXT", payload: event.target.value });
              }}
              placeholder={t("components.inputs.searchEstablishment")}
            />
          </Box>

          <Box>
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" gap={1}>
                <VmVButton
                  onClick={() => {
                    dispatch({ type: "RESET", payload: "" });
                  }}
                  type="primary"
                >
                  {t("common.reset")}
                </VmVButton>
                <VmVSelect
                  label={t("components.filters.allRegions")}
                  options={formatDropDownData(RegionDropDowData)}
                  value={filters.region}
                  onChange={(value) => {
                    dispatch({ type: "SET_REGION", payload: value });
                  }}
                />
              </Box>
              <Box>
                <VmVButton
                  onClick={() => {}}
                  type="secondary"
                  RightIcon={<CSV />}
                >
                  <CSVLink
                    data={formatData(data)}
                    filename={"data.csv"}
                    style={{
                      color: "inherit",
                      textDecoration: "inherit",
                      fontFamily: "inherit",
                    }}
                    target="_blank"
                  >
                    {t("common.export")}
                  </CSVLink>
                </VmVButton>
              </Box>
            </Box>
          </Box>
          <VmVDataArray
            cells={translatedCells}
            rows={filterData(formatData(data), filters)}
            showFooter
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ParticipationsAndRegionsDetails;

export const ParticipationsAndRegionsDetailsChart = ({
  home,
}: {
  home?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const { data: graphData } = useQuery({
    queryKey: ["regions/EHPAD/graph"],
    queryFn: async () => await fetchData("regions/EHPAD/graph"),
  });

  // verifier le data du useQuery
  // useEffect(() => {
  //   console.log("graphData", graphData);
  // }, [graphData]);

  const calculateTotal = (data: any): number => {
    return (
      parseInt(data?.totalDispoHotellerieASH ?? 0) +
      parseInt(data?.totalDispoHotellerieAccueilAdministration ?? 0) +
      parseInt(data?.totalDispoHotellerieAnimation ?? 0) +
      parseInt(data?.totalDispoHotellerieServer ?? 0) +
      parseInt(data?.totalDispoMaintenance ?? 0) +
      parseInt(data?.totalDispoRestauration ?? 0)
    );
  };

  return (
    <Box
      alignSelf="center"
      display="flex"
      flexDirection="column"
      gap={1}
      mb={8}
      width="100%"
    >
      <Box
        mt={4}
        alignSelf="center"
        width="100%"
        display="flex"
        flexDirection="column"
        mb={4}
        gap={8}
      >
        <Grid container justifyContent="center" alignItems="center">
          {graphData?.regionsList.map((region: any, index) => {
            return (
              <Grid item xs={3} key={index}>
                <VmVPie
                  total={graphData?.totalNombreInscirptions}
                  color={home === true ? colors.yellow : colors.orange}
                  area={region?._id}
                  participantsNumber={region.count}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Box mt={4}>
        <VmVLabel
          color={home === true ? colors.yellow : colors.orange}
          label={t("pages.participationsAndRegions.ehpadEnrolled")}
        />
      </Box>
    </Box>
  );
};
